export const LIST_PAGE_SIZE = 10;
export const SCROLL_LOAD_THRESHOLD_PX = 80;
export const OPENED_ITEM_TOP_OFFSET_PX = 12;

export const normalizeListItems = (list) => {
    if (Array.isArray(list?.resp)) {
        return list.resp;
    }
    if (Array.isArray(list?.transactions)) {
        return list.transactions;
    }
    if (Array.isArray(list)) {
        return list;
    }
    return [];
};

export const getTransactionKey = (item) =>
    item?.transactionUlid || item?.transaction_ulid || item?.transaction_id || item?.id;

export const getRfqKey = (item) => item?.rfq_ulid || item?.id;

export const getListItemKey = (item, type) => (type === 'transaction' ? getTransactionKey(item) : getRfqKey(item));

export const isOpenedListItem = (item, openedId, type) => {
    if (!item || openedId == null || openedId === '') {
        return false;
    }
    const opened = String(openedId);
    if (type === 'transaction') {
        return [item.transactionUlid, item.transaction_ulid, item.transactionId, item.id]
            .filter((value) => value != null && value !== '')
            .some((value) => String(value) === opened);
    }
    return [item.rfq_ulid, item.id]
        .filter((value) => value != null && value !== '')
        .some((value) => String(value) === opened);
};

export const toTransactionListItem = (item) => {
    if (!item || typeof item !== 'object') {
        return null;
    }
    const transactionUlid = item.transactionUlid || item.transaction_ulid;
    if (!transactionUlid && item.id == null && item.transactionId == null) {
        return null;
    }
    return {
        ...item,
        transactionUlid,
        transactionId: item.transactionId ?? item.id,
        buyer: item.buyer || item.buyer_name,
        port: item.port || item.port_name,
    };
};

export const ensureOpenedInList = (items, openedItem, openedId) => {
    const list = Array.isArray(items) ? items : [];
    if (!openedId) {
        return list;
    }
    if (list.some((item) => isOpenedListItem(item, openedId, 'transaction'))) {
        return list;
    }
    const seeded = toTransactionListItem(openedItem);
    if (!seeded || !isOpenedListItem(seeded, openedId, 'transaction')) {
        return list;
    }
    return [{ ...seeded, isOpenedSeed: true }, ...list];
};

export const isSeededOpenedItem = (item) => Boolean(item?.isOpenedSeed);

export const findOpenedItemIndex = (items, openedId, type) => {
    const list = Array.isArray(items) ? items : [];
    if (!openedId) {
        return -1;
    }
    return list.findIndex((item) => isOpenedListItem(item, openedId, type));
};

export const getVisibleCountForIndex = (openedIndex, visibleCount, pageSize = LIST_PAGE_SIZE) => {
    const current = visibleCount || 0;
    if (openedIndex == null || openedIndex < 0 || openedIndex < current) {
        return current;
    }
    return Math.ceil((openedIndex + 1) / pageSize) * pageSize;
};

export const shouldScrollToOpenedItem = ({ openedIndex, isSeeded, hasScrolled }) =>
    openedIndex >= 0 && !isSeeded && !hasScrolled;

export const isOpenedItemInView = ({ containerTop = 0, containerBottom = 0, itemTop = 0, itemBottom = 0 }) =>
    itemTop >= containerTop && itemBottom <= containerBottom;

export const getScrollTopForOpenedItem = ({
    containerScrollTop = 0,
    containerTop = 0,
    itemTop = 0,
    maxScrollTop = 0,
    topOffset = OPENED_ITEM_TOP_OFFSET_PX,
}) => {
    const target = containerScrollTop + (itemTop - containerTop) - topOffset;
    const upperBound = Math.max(maxScrollTop, 0);
    return Math.min(Math.max(target, 0), upperBound);
};

export const filterListBySearch = (items, type, searchText) => {
    const list = Array.isArray(items) ? items : [];
    const query = (searchText || '').trim().toLowerCase();
    if (!query) {
        return list;
    }
    if (type === 'transaction') {
        return list.filter((item) => {
            const transactionId = item.transactionId?.toString().toLowerCase() || '';
            const rfqId = (item.rfqId || item.rfqID)?.toString().toLowerCase() || '';
            return transactionId.includes(query) || rfqId.includes(query);
        });
    }
    return list.filter((item) => {
        const id = item.id?.toString().toLowerCase() || '';
        const orgName = item.organization_name?.toLowerCase() || '';
        return id.includes(query) || orgName.includes(query);
    });
};

export const mergeUniqueByKey = (existing, incoming, type) => {
    const current = Array.isArray(existing) ? existing : [];
    const next = Array.isArray(incoming) ? incoming : [];
    const seen = new Set(current.map((item) => String(getListItemKey(item, type) ?? '')));
    const merged = [...current];
    next.forEach((item) => {
        const key = String(getListItemKey(item, type) ?? '');
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        merged.push(item);
    });
    return merged;
};

/** Backend orders transactions by `tm.id DESC`; keep that order when pages arrive out of sequence. */
export const sortTransactionsByIdDesc = (items) => {
    const list = Array.isArray(items) ? items : [];
    return [...list].sort((a, b) => {
        const left = Number(a?.transactionId ?? a?.id);
        const right = Number(b?.transactionId ?? b?.id);
        if (!Number.isFinite(left) || !Number.isFinite(right)) {
            return 0;
        }
        return right - left;
    });
};

export const sliceVisibleItems = (items, visibleCount) => {
    const list = Array.isArray(items) ? items : [];
    if (visibleCount == null || visibleCount >= list.length) {
        return list;
    }
    return list.slice(0, Math.max(visibleCount, 0));
};

export const getScrollLoadDirection = (el, threshold = SCROLL_LOAD_THRESHOLD_PX) => {
    if (!el) {
        return null;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) {
        return null;
    }
    if (scrollHeight - scrollTop - clientHeight <= threshold) {
        return 'down';
    }
    if (scrollTop <= threshold) {
        return 'up';
    }
    return null;
};

export const buildTransactionSidePanelList = ({ list, openedItem, openedId, searchText, type }) => {
    const normalized = normalizeListItems(list);
    const withOpened = type === 'transaction' ? ensureOpenedInList(normalized, openedItem, openedId) : normalized;
    return filterListBySearch(withOpened, type, searchText);
};
